import { DashboardTabType } from "gql/apollo/graphql";
import { useCallback } from "react";
import { useMixpanel } from "react-mixpanel-browser";
import deepCopy from "./deepCopy";

interface FollowupClickData {
	incident_id: string;
	type: string;
	text: string;
	is_new_dash?: boolean;
}

interface LogToMixPanelData {
	after?: string;
	before?: string;
	incident_id?: string;
	is_new_dash?: boolean;
	keywords?: string[];
	locations?: {
		_id: string;
		key: string;
	}[];
	source?: string;
	stream_id?: string;
	summary?: string;
	token?: string;
}

interface UnreadFilterToggled {
	enabled?: boolean;
	is_new_dash?: boolean;
	position?: string;
}

interface TabCreated {
	type: DashboardTabType;
	is_new_dash?: boolean;
}

interface TabDeleted {
	type: DashboardTabType;
	is_new_dash?: boolean;
}

interface TabLimitReached {
	type: string;
	limit: number;
	is_new_dash?: boolean;
}

interface TabReorderStarted {
	tab_id: string;
	original_index: number;
	total_tabs: number;
	tab_type: string;
	is_new_dash?: boolean;
}

interface TabReorderCompleted {
	tab_id: string;
	original_index: number;
	new_index: number;
	tab_ids_before: string[];
	tab_ids_after: string[];
	tab_type: string;
	is_new_dash?: boolean;
}

type TabReorderCancelled = TabReorderCompleted;

interface ColumnCreated {
	total_columns: number;
	is_new_dash?: boolean;
}

interface ColumnDeleted {
	total_columns: number;
	is_new_dash?: boolean;
}

type OverflowButtonClicked = {
	selected_tab_index: number;
	selected_tab_id: string;
	total_tabs: number;
	tab_ids: string[];
	is_new_dash?: boolean;
};

type SummaryModalAction = {
	action: string;
	is_new_dash?: boolean;
};

type TimeFilterChanged = {
	value: string;
	is_new_dash?: boolean;
};

type StreamsListAction = {
	source: string;
	is_new_dash?: boolean;
};

type AtlasPdfExportClicked = {
	source?: string;
	is_new_dash?: boolean;
};

type AtlasCsvExportLimit = {
	limit: number;
	total_incidents: number;
	action?: "export" | "cancel";
	source?: string;
	is_new_dash?: boolean;
};

type LogTimeOnTab = {
	duration_seconds: number;
	tab_type: DashboardTabType;
	is_new_dash?: boolean;
};

type MixpanelData =
	| FollowupClickData
	| LogToMixPanelData
	| UnreadFilterToggled
	| SummaryModalAction
	| TabCreated
	| TabDeleted
	| TabLimitReached
	| TabReorderStarted
	| TabReorderCompleted
	| TabReorderCancelled
	| ColumnCreated
	| ColumnDeleted
	| TimeFilterChanged
	| OverflowButtonClicked
	| StreamsListAction
	| LogTimeOnTab
	| AtlasPdfExportClicked
	| AtlasCsvExportLimit;

const useLogToMixPanel = () => {
	const mixpanel = useMixpanel();

	const logToMixpanel = useCallback(
		(event: string, data: MixpanelData) => {
			if (mixpanel.config?.token) {
				const dataCopy = deepCopy(data);
				dataCopy.is_new_dash = true;
				mixpanel.track(event, dataCopy);
			} else {
				console.info(`Event ${event} skipped tracking`);
			}
		},
		[mixpanel]
	);

	return { logToMixpanel };
};

export default useLogToMixPanel;
